import * as React from 'react';
import { TagContainer, Tag as StyledTag, TagItemProps, AddTagInput, MoreTags } from '~/ui/components/Tags/Tags.styled';
import { CloseAlt } from '~/ui/assets/icons';
import { Popover } from 'antd';

import { TagOptions } from '~/ui/components/Tags/TagOptions';
import { PropsWithChildren, useEffect, useState } from 'react';
import { ColorsTypes } from '~/ui/theme/colors';
import { normalizeTagName } from '~/common/utils/formatTagName';
import { TagPopover } from './TagPopover';

export interface TagItem extends Omit<TagItemProps, 'small' | 'rounded'> {
  id: string;
  tagId?: string;
  name: string;
  creationDate?: string;
}

export type TagsProps = {
  options?: TagItem[];
  tags: TagItem[];
  closable?: boolean;
  onClose?: (tag: TagItem) => void;
  onAdd?: (tag: TagItem) => void;
  onCreate?: (newTag: string) => void;
  small?: boolean;
  rounded?: boolean;
  editable?: boolean;
  moreTagsBgColor?: string;
  moreTagsTextColor?: string;
  maxVisible?: number;
};

export function Tags({
  options,
  tags,
  small = false,
  closable,
  onClose,
  onAdd,
  rounded,
  editable,
  onCreate,
  moreTagsBgColor,
  moreTagsTextColor,
  maxVisible = 2,
  ...props
}: TagsProps) {
  const [currentTags, setCurrentTags] = useState<TagItem[]>([]);
  const [isAddTagPopoverOpen, setIsAddTagPopoverOpen] = useState(false);

  const visibleTags = currentTags.slice(0, maxVisible);
  const hiddenTags = currentTags.slice(maxVisible);

  const canShowAddTag = editable && options?.length;

  const handleAddTag = (tag: TagItem) => {
    setIsAddTagPopoverOpen(false);
    onAdd?.(tag);
  };

  const handleCreateTag = (tagName: string) => {
    setIsAddTagPopoverOpen(false);
    onCreate?.(tagName);
  };

  const handleClose = (tag: TagItem) => () => {
    onClose?.(tag);
  };

  useEffect(() => {
    const sortedTags = tags
      .map((tag) => ({
        ...tag,
        name: normalizeTagName(tag.name),
      }))
      .sort((a, b) => new Date(a?.creationDate).getTime() - new Date(b?.creationDate).getTime());

    setCurrentTags(sortedTags);
  }, [tags]);

  const renderTag = (tag: TagItem) => {
    const { colorPalette, textColor, bgColor } = tag;

    return (
      <StyledTag
        key={tag.id}
        colorPalette={colorPalette}
        textColor={textColor}
        bgColor={bgColor}
        small={small}
        closable={closable}
        rounded={rounded}
        onClose={() => onClose?.(tag)}
      >
        {tag.name}
        {closable && <CloseAlt width={10} height={10} onClick={handleClose(tag)} />}
      </StyledTag>
    );
  };

  const renderAddTagButton = (popoverOpen: boolean, setPopoverOpen: (val: boolean) => void) => (
    <Popover
      trigger="click"
      placement="bottomLeft"
      overlayClassName="mt-popover-no-arrow mt-popover-no-padding"
      visible={popoverOpen}
      onVisibleChange={setPopoverOpen}
      content={
        <TagOptions currentTags={currentTags} options={options} onAdd={handleAddTag} onCreate={handleCreateTag} />
      }
    >
      <AddTagInput small={small} onClick={() => setPopoverOpen(true)} className="add-tag">
        + Tag
      </AddTagInput>
    </Popover>
  );

  return (
    <TagContainer className="tags" {...props}>
      {visibleTags.map(renderTag)}

      {hiddenTags.length > 0 ? (
        <TagPopover
          tags={tags}
          editable
          options={options}
          onAdd={handleAddTag}
          onCreate={handleCreateTag}
          onRemove={handleClose}
          closable={closable}
        >
          <MoreTags small={small} rounded={rounded} bgColor={moreTagsBgColor} textColor={moreTagsTextColor}>
            +{hiddenTags.length}
          </MoreTags>
        </TagPopover>
      ) : null}

      {canShowAddTag ? renderAddTagButton(isAddTagPopoverOpen, setIsAddTagPopoverOpen) : null}
    </TagContainer>
  );
}

export type TagProps = Omit<TagItemProps, 'colorPalette'> & { color?: ColorsTypes };

export function Tag({ children, color, ...props }: PropsWithChildren<TagProps>) {
  return (
    <StyledTag rounded small {...props} colorPalette={color}>
      {children}
    </StyledTag>
  );
}
